import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Button, Form } from "react-bootstrap";
import { ProdutoOportunidade } from "../../../../types/orcamento";
import { apiService } from "../../../../services/api";
import { Fornecedor } from "../../../../types/fornecedor";
import { OrcamentoDetalhado, OrcamentoProdutoDetalhado } from "../../../../types/orcamentos_multiplos";
import ModalEditarProduto from "./ModalEditarProduto";
import CalculadoraPrecificacao from "./CalculadoraPrecificacao";
import GerarPropostaPDF from "./GerarPropostaPDF";
import ModalNovoOrcamento from "./orcamentos/ModalNovoOrcamento";

interface OrcamentoListProps {
    opportunityId: number;
    etapaAtual: number;
    funilAtual: number;
    formatCurrency: (value: number | string) => string;
    onUpdateComplete: () => void;
    // Muda quando outra aba altera orçamentos, para esta lista recarregar
    refreshKey?: number;
}

const OrcamentoList: React.FC<OrcamentoListProps> = ({
    opportunityId,
    etapaAtual,
    funilAtual,
    formatCurrency,
    onUpdateComplete,
    refreshKey = 0
}) => {
    const [produtos, setProdutos] = useState<ProdutoOportunidade[]>([]);
    const [produtosOrdenados, setProdutosOrdenados] = useState<ProdutoOportunidade[]>([]);
    const [loading, setLoading] = useState(true);
    const jaCarregouRef = useRef(false);
    const [totais, setTotais] = useState<{
        total_referencia: number;
        total_orcado: number;
        total_proposta: number;
        total_geral: number;
    }>({
        total_referencia: 0,
        total_orcado: 0,
        total_proposta: 0,
        total_geral: 0
    });
    const [configuracao, setConfiguracao] = useState<{
        permite_editar_fornecedor: boolean;
        permite_editar_valor_orcado: boolean;
        permite_editar_valor_proposta: boolean;
    }>({
        permite_editar_fornecedor: false,
        permite_editar_valor_orcado: false,
        permite_editar_valor_proposta: false
    });
    const [selectedProduto, setSelectedProduto] = useState<ProdutoOportunidade | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditNumeroEditalModal, setShowEditNumeroEditalModal] = useState(false);
    const [numeroEditalInput, setNumeroEditalInput] = useState("");
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [editingNumeroEdital, setEditingNumeroEdital] = useState(false);

    // Estados para orçamentos
    const [orcamentos, setOrcamentos] = useState<OrcamentoDetalhado[]>([]);
    const [produtosPorOrcamento, setProdutosPorOrcamento] = useState<{ [key: number]: OrcamentoProdutoDetalhado[] }>({});
    const [showModalNovoOrcamento, setShowModalNovoOrcamento] = useState(false);

    // Estados para o card de informações
    const [produtoInfoVisivel, setProdutoInfoVisivel] = useState<number | null>(null);
    const infoCardRef = useRef<HTMLDivElement>(null);

    // Função para ordenar produtos por número de edital ou id
    const ordenarProdutos = (produtosParaOrdenar: ProdutoOportunidade[]) => {
        const getNumeroEditalFromString = (edital: string | null | undefined): number => {
            if (!edital || typeof edital !== 'string' || edital.trim() === '') {
                return 0;
            }
            const match = edital.match(/\d+/);
            if (!match || !match[0]) {
                return 0;
            }
            const parsedNum = parseInt(match[0], 10);
            return isNaN(parsedNum) ? 0 : parsedNum;
        };

        return [...produtosParaOrdenar].sort((a, b) => {
            const numeroEditalA = getNumeroEditalFromString(a.numero_edital);
            const numeroEditalB = getNumeroEditalFromString(b.numero_edital);

            const aTemEditalValido = numeroEditalA !== 0;
            const bTemEditalValido = numeroEditalB !== 0;

            if (aTemEditalValido && !bTemEditalValido) {
                return -1;
            }
            if (!aTemEditalValido && bTemEditalValido) {
                return 1;
            }

            if (aTemEditalValido && bTemEditalValido) {
                if (numeroEditalA !== numeroEditalB) {
                    return numeroEditalA - numeroEditalB;
                }
                return a.id - b.id;
            }

            return a.id - b.id;
        });
    };

    // Atualizar produtos ordenados sempre que a lista de produtos mudar
    useEffect(() => {
        setProdutosOrdenados(ordenarProdutos(produtos));
    }, [produtos]);

    // Fechar o card ao clicar fora dele
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (infoCardRef.current &&
                !infoCardRef.current.contains(event.target as Node) &&
                !(event.target as Element).classList.contains('info-button')) {
                setProdutoInfoVisivel(null);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Função para mostrar/esconder o card de informações
    const toggleInfoCard = (e: React.MouseEvent, produtoId: number) => {
        e.stopPropagation();
        e.preventDefault();
        setProdutoInfoVisivel(produtoInfoVisivel === produtoId ? null : produtoId);
    };

    // Função para formatar números (quantidades) que vêm como string do banco
    const formatQuantity = (value: string | number) => {
        // Converter para número caso seja string
        const numValue = typeof value === 'string' ? parseFloat(value) : value;

        // Verifica se o valor é um número inteiro
        if (Number.isInteger(numValue)) {
            // Formata sem casas decimais
            return numValue.toString();
        } else {
            // Formata com casas decimais (usando vírgula)
            return numValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2
            });
        }
    };

    // Função para obter a cor da margem com base no valor
    const getMargemColor = (margem: number | null): string => {
        if (margem === null) return '';
        if (margem <= 8) return 'bg-danger';
        if (margem < 15) return 'bg-warning';
        return 'bg-success';
    };


    const formatarPorcentagem = (valor: number | null | string): string => {
        if (valor === null || valor === undefined) return '-';

        // Garantir que valor seja um número
        const numValue = typeof valor === 'string' ? parseFloat(valor) : valor;

        // Verificar se é um número válido
        if (isNaN(numValue)) return '-';

        return numValue.toFixed(2) + '%';
    };

    const handleProdutoValueChange = (produtoId: number, novoValorProposta: number | null) => {
        // Atualizar o produto localmente
        setProdutos(prevProdutos =>
            prevProdutos.map(produto =>
                produto.id === produtoId
                    ? { ...produto, valor_proposta: novoValorProposta }
                    : produto
            )
        );

        // Notificar o componente pai (se necessário)
        onUpdateComplete();
    };

    // Carregar produtos da oportunidade
    const loadProdutos = async () => {
        try {
            // Spinner só na primeira carga. Nas recargas (editar/excluir) a lista
            // continua visível e é atualizada no lugar, preservando a rolagem.
            if (!jaCarregouRef.current) {
                setLoading(true);
            }

            // Buscar produtos
            const produtosRes = await apiService.getProdutosOportunidade(opportunityId);

            if (produtosRes.success) {
                setProdutos(produtosRes.data);

                // Calcular totais manualmente a partir dos produtos
                const totalRef = produtosRes.data.reduce((acc, p) => acc + (p.quantidade * p.valor_referencia), 0);
                const totalOrcado = produtosRes.data.reduce((acc, p) => {
                    if (p.valor_orcado !== null) {
                        return acc + (p.quantidade * p.valor_orcado);
                    }
                    return acc;
                }, 0);
                const totalProposta = produtosRes.data.reduce((acc, p) => {
                    if (p.valor_proposta !== null) {
                        return acc + (p.quantidade * p.valor_proposta);
                    }
                    return acc;
                }, 0);

                // NOVO: Total geral seguindo a lógica da coluna individual
                const totalGeral = produtosRes.data.reduce((acc, p) => {
                    const valorUsar = p.valor_proposta !== null ? p.valor_proposta : p.valor_referencia;
                    return acc + (p.quantidade * valorUsar);
                }, 0);

                setTotais({
                    total_referencia: totalRef,
                    total_orcado: totalOrcado,
                    total_proposta: totalProposta,
                    total_geral: totalGeral
                });
            }

            // Buscar configurações de etapa
            const configRes = await apiService.getConfiguracaoEtapaOrcamentoByFunilEtapa(funilAtual, etapaAtual);

            if (configRes.success && configRes.data) {
                setConfiguracao({
                    permite_editar_fornecedor: configRes.data.permite_editar_fornecedor,
                    permite_editar_valor_orcado: configRes.data.permite_editar_valor_orcado,
                    permite_editar_valor_proposta: configRes.data.permite_editar_valor_proposta
                });
            }

            // Buscar fornecedores (ao invés de contatos tipo PJ)
            const fornecedoresRes = await apiService.getFornecedores({
                tipo_entidade: 'PJ'
            });

            if (fornecedoresRes.success) {
                setFornecedores(fornecedoresRes.data);
            }

            // Carregar orçamentos da oportunidade
            const orcamentosRes = await apiService.getOrcamentos({
                id_oportunidade: opportunityId
            });

            if (orcamentosRes.success && Array.isArray(orcamentosRes.data)) {
                const orcamentosOrdenados = orcamentosRes.data.sort((a, b) => b.versao - a.versao);
                setOrcamentos(orcamentosOrdenados);

                // Carregar produtos de todos os orçamentos em paralelo
                const produtosOrcResults = await Promise.all(
                    orcamentosOrdenados.map(orc =>
                        apiService.getOrcamentoProdutos({ id_orcamento: orc.id }).catch(err => {
                            console.error(`Erro ao carregar produtos do orçamento ${orc.id}:`, err);
                            return { data: [] };
                        })
                    )
                );
                const produtosPorOrc: { [key: number]: OrcamentoProdutoDetalhado[] } = {};
                orcamentosOrdenados.forEach((orc, index) => {
                    const res = produtosOrcResults[index];
                    if ('data' in res && Array.isArray(res.data)) {
                        produtosPorOrc[orc.id] = res.data;
                    }
                });
                setProdutosPorOrcamento(produtosPorOrc);
            }

        } catch (error) {
            console.error("Erro ao carregar produtos:", error);
            toast.error("Erro ao carregar produtos da oportunidade");
        } finally {
            jaCarregouRef.current = true;
            setLoading(false);
        }
    };

    // Carregar dados ao montar o componente
    useEffect(() => {
        if (opportunityId) {
            loadProdutos();
        }
    }, [opportunityId, etapaAtual, funilAtual, refreshKey]);

    const handleCalculadoraUpdate = () => {
        loadProdutos();
        onUpdateComplete();
    };

    // Funções para manipular produtos
    const handleEdit = (produto: ProdutoOportunidade) => {
        setSelectedProduto(produto);
        setShowEditModal(true);
    };

    const handleDelete = (produto: ProdutoOportunidade) => {
        setSelectedProduto(produto);
        setShowDeleteModal(true);
    };

    // Função para editar o número do edital
    const handleEditNumeroEdital = (produto: ProdutoOportunidade) => {
        setSelectedProduto(produto);
        setNumeroEditalInput(produto.numero_edital || "");
        setShowEditNumeroEditalModal(true);
    };

    // Função para salvar o número do edital
    const saveNumeroEdital = async () => {
        if (!selectedProduto) return;

        try {
            setEditingNumeroEdital(true);

            const response = await apiService.updateProdutoOportunidade(selectedProduto.id, {
                numero_edital: numeroEditalInput
            });

            if (response.success) {
                toast.success("Número do edital atualizado com sucesso!");
                loadProdutos();
                setShowEditNumeroEditalModal(false);
            } else {
                toast.error("Erro ao atualizar número do edital");
            }
        } catch (error) {
            console.error("Erro ao atualizar número do edital:", error);
            toast.error("Ocorreu um erro ao atualizar o número do edital");
        } finally {
            setEditingNumeroEdital(false);
        }
    };

    const confirmDelete = async () => {
        if (!selectedProduto) return;

        try {
            await apiService.deleteProdutoOportunidade(selectedProduto.id);
            toast.success("Produto removido com sucesso!");
            loadProdutos();
            onUpdateComplete();
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Erro ao remover produto:", error);
            toast.error("Erro ao remover produto");
        }
    };

    const handleProdutoSaved = () => {
        loadProdutos();
        onUpdateComplete();
        setShowEditModal(false);
    };

    // Determinar ícones/cores para diferentes estados
    const getStatusIcon = (valor: number | null) => {
        if (valor === null) return <i className="ti ti-circle-x text-muted"></i>;
        return <i className="ti ti-check text-success"></i>;
    };

    // Função para copiar descrição do produto
    const copyDescricao = (produto: ProdutoOportunidade) => {
        const descricaoEspecifica = (produto as any).descricao_especifica || 'Sem descrição específica';
        const textoParaCopiar = `${produto.nome_produto}\n\nDescrição: ${descricaoEspecifica}`;
        navigator.clipboard.writeText(textoParaCopiar);
        toast.success("Descrição copiada para a área de transferência!");
    };

    // Função para calcular o menor preço de um produto entre todos os orçamentos
    const calcularMenorPreco = (produto: ProdutoOportunidade): number | null => {
        const precos: number[] = [];

        // Adicionar valores de todos os orçamentos
        orcamentos.forEach(orc => {
            const produtosDoOrc = produtosPorOrcamento[orc.id] || [];
            const produtoOrc = produtosDoOrc.find(
                p => p.id_produto_oportunidade === produto.id
            );
            if (produtoOrc) {
                precos.push(parseFloat(produtoOrc.valor_unitario));
            }
        });

        return precos.length > 0 ? Math.min(...precos) : null;
    };

    // Função para verificar se um valor é o menor preço
    const isMenorPreco = (valor: number, menorPreco: number | null): boolean => {
        return menorPreco !== null && Math.abs(valor - menorPreco) < 0.0001;
    };

    return (
        <>
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="fw-semibold">Produtos e Orçamentos</h4>
                    <div className="d-flex gap-2">
                        {/* Botão Novo Orçamento */}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowModalNovoOrcamento(true)}
                        >
                            <i className="ti ti-plus me-1" />
                            Novo Orçamento
                        </button>
                        {/* Botão para gerar proposta, mostrado apenas se houver produtos */}
                        {produtos.length > 0 && (
                            <GerarPropostaPDF
                                opportunityId={opportunityId}
                                produtos={produtos}
                                formatCurrency={formatCurrency}
                            />
                        )}
                        {produtos.length > 0 && (
                            <CalculadoraPrecificacao
                                produtos={produtos}
                                formatCurrency={formatCurrency}
                                onUpdateComplete={handleCalculadoraUpdate}
                            />
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : produtos.length === 0 ? (
                        <div className="alert alert-info">
                            Nenhum produto registrado para esta oportunidade.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped custom-table mb-0">
                                <thead>
                                    <tr>
                                        <th>N° Edital</th>
                                        <th>Produto</th>
                                        <th>Marca</th>
                                        <th>Descrição</th>
                                        <th>Qtd.</th>
                                        <th>Unidade</th>
                                        <th className="text-center">Valor Ref.</th>
                                        {/* Colunas dinâmicas para cada orçamento */}
                                        {orcamentos.map(orc => (
                                            <th key={`orc-header-${orc.id}`} className="text-center">
                                                <div
                                                    style={{
                                                        maxWidth: '120px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        cursor: 'help'
                                                    }}
                                                    title={orc.titulo}
                                                >
                                                    {orc.titulo}
                                                </div>
                                            </th>
                                        ))}
                                        <th className="text-center">Menor Preço</th>
                                        <th>Valor Proposta</th>
                                        <th>Total</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {produtosOrdenados.map((produto) => {
                                        // Calcular o valor total com base no utiliza_valor_proposta da oportunidade
                                        const total = produto.valor_proposta !== null
                                            ? produto.quantidade * produto.valor_proposta
                                            : produto.quantidade * produto.valor_referencia;

                                        return (
                                            <tr key={produto.id}>
                                                {/* Coluna para Número do Edital - agora é a primeira */}
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            style={{
                                                                maxWidth: '80px',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                            title={produto.numero_edital || ''}
                                                        >
                                                            {produto.numero_edital || <span className="text-muted">-</span>}
                                                        </div>
                                                        <button
                                                            className="ms-2 btn btn-link btn-sm p-0"
                                                            onClick={() => handleEditNumeroEdital(produto)}
                                                            title="Editar número do edital"
                                                        >
                                                            <i className="ti ti-pencil-alt" style={{ fontSize: '14px' }}></i>
                                                        </button>
                                                    </div>
                                                </td>
                                                <td style={{ position: 'relative', overflow: 'visible' }}>
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            style={{
                                                                maxWidth: '140px',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                            title={produto.nome_produto}
                                                        >
                                                            {produto.nome_produto}
                                                        </div>
                                                        <button
                                                            className="ms-2 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center info-button"
                                                            onClick={(e) => toggleInfoCard(e, produto.id)}
                                                            style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold',
                                                                border: 'none',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Ver informações detalhadas"
                                                        >
                                                            i
                                                        </button>

                                                        {produtoInfoVisivel === produto.id && (
                                                            <div
                                                                ref={infoCardRef}
                                                                className="position-absolute start-100 top-0 ms-2 bg-white rounded shadow-lg border border-gray-200"
                                                                style={{
                                                                    zIndex: 9999,
                                                                    width: '280px',
                                                                    maxWidth: '90vw'
                                                                }}
                                                            >
                                                                <div className="p-3">
                                                                    <h6
                                                                        className="fw-bold text-primary mb-2"
                                                                        style={{
                                                                            maxWidth: '100%',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                        title={produto.nome_produto}
                                                                    >
                                                                        {produto.nome_produto}
                                                                    </h6>
                                                                    <p className="small mb-3" style={{ color: '#555' }}>
                                                                        {(produto as any).descricao_especifica || 'Sem descrição específica'}
                                                                    </p>
                                                                    {produto.marca && (
                                                                        <p className="small mb-2">
                                                                            <strong>Marca:</strong> {produto.marca}
                                                                        </p>
                                                                    )}
                                                                    <div className="d-flex justify-content-end mt-2">
                                                                        <button
                                                                            onClick={() => copyDescricao(produto)}
                                                                            className="btn btn-sm btn-outline-primary me-2"
                                                                        >
                                                                            <i className="ti ti-copy"></i> Copiar
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setProdutoInfoVisivel(null)}
                                                                            className="btn btn-sm btn-light"
                                                                        >
                                                                            <i className="ti ti-x"></i> Fechar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div
                                                        style={{
                                                            maxWidth: '120px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                        title={(produto as any).marca ?? 'Sem marca'}
                                                    >
                                                        {(produto as any).marca ? (produto as any).marca : <span className="text-muted">-</span>}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div
                                                        style={{
                                                            maxWidth: '150px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                        title={(produto as any).descricao_especifica ?? 'Sem descrição específica'}
                                                    >
                                                        {(produto as any).descricao_especifica ? (produto as any).descricao_especifica : <span className="text-muted">-</span>}
                                                    </div>
                                                </td>
                                                <td>{formatQuantity(produto.quantidade)}</td>
                                                <td>{produto.unidade_medida || "UN"}</td>
                                                <td className="text-center"><strong>{formatCurrency(produto.valor_referencia)}</strong></td>

                                                {(() => {
                                                    const menorPreco = calcularMenorPreco(produto);

                                                    return (
                                                        <>
                                                            {/* Colunas dinâmicas para cada orçamento */}
                                                            {orcamentos.map(orc => {
                                                                const produtosDoOrc = produtosPorOrcamento[orc.id] || [];
                                                                const produtoOrc = produtosDoOrc.find(
                                                                    p => p.id_produto_oportunidade === produto.id
                                                                );

                                                                const valorUnitario = produtoOrc ? parseFloat(produtoOrc.valor_unitario) : null;
                                                                const ehMenorPreco = valorUnitario !== null && isMenorPreco(valorUnitario, menorPreco);
                                                                const refValue = Number(produto.valor_referencia);
                                                const ehMaiorQueRef = valorUnitario !== null && refValue !== 0 && valorUnitario > refValue;

                                                                const borderSeparador = '2px solid #fff';
                                                                const tdStyle = ehMaiorQueRef
                                                                    ? (ehMenorPreco
                                                                        ? { backgroundColor: '#f8d7da', fontWeight: 'bold', color: '#721c24', borderRight: borderSeparador, borderLeft: borderSeparador }
                                                                        : { backgroundColor: '#f8d7da', color: '#721c24', borderRight: borderSeparador, borderLeft: borderSeparador })
                                                                    : (ehMenorPreco
                                                                        ? { backgroundColor: '#d4edda', fontWeight: 'bold', color: '#155724', borderRight: borderSeparador, borderLeft: borderSeparador }
                                                                        : {});

                                                                return (
                                                                    <td
                                                                        key={`prod-${produto.id}-orc-${orc.id}`}
                                                                        className="text-center"
                                                                        style={tdStyle}
                                                                    >
                                                                        {produtoOrc
                                                                            ? (
                                                                                <span>
                                                                                    {formatCurrency(valorUnitario!)}
                                                                                    {ehMenorPreco && (
                                                                                        <i
                                                                                            className="ti ti-arrow-down ms-1"
                                                                                            style={{ fontSize: '11px' }}
                                                                                            title={ehMaiorQueRef ? "Menor orçado, mas acima do valor de referência" : "Menor preço orçado"}
                                                                                        />
                                                                                    )}
                                                                                </span>
                                                                            )
                                                                            : <span className="text-muted">-</span>
                                                                        }
                                                                    </td>
                                                                );
                                                            })}

                                                            {/* Coluna Menor Preço */}
                                                            <td className="text-center">
                                                                {menorPreco !== null
                                                                    ? formatCurrency(menorPreco)
                                                                    : <span className="text-muted">-</span>
                                                                }
                                                            </td>
                                                        </>
                                                    );
                                                })()}

                                                <td className={configuracao.permite_editar_valor_proposta ? 'text-success' : ''}>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {produto.valor_proposta !== null
                                                            ? formatCurrency(produto.valor_proposta)
                                                            : '-'}

                                                        {/* Exibir o badge de margem aqui */}
                                                        {produto.margem_lucro !== null && produto.valor_proposta !== null && (
                                                            <span className={`badge ${getMargemColor(produto.margem_lucro)} rounded-pill`}>
                                                                {formatarPorcentagem(produto.margem_lucro)}
                                                            </span>
                                                        )}

                                                        {configuracao.permite_editar_valor_proposta &&
                                                            <i className="ti ti-pencil-alt" style={{ fontSize: '12px' }}></i>}
                                                    </div>
                                                </td>
                                                <td><strong>{formatCurrency(total)}</strong></td>
                                                <td>
                                                    <div className="d-flex">
                                                        <Link
                                                            className="btn btn-sm btn-outline-primary me-2"
                                                            to="#"
                                                            onClick={() => handleEdit(produto)}
                                                        >
                                                            <i className="ti ti-edit"></i>
                                                        </Link>
                                                        <Link
                                                            className="btn btn-sm btn-outline-danger"
                                                            to="#"
                                                            onClick={() => handleDelete(produto)}
                                                        >
                                                            <i className="ti ti-trash"></i>
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light">
                                        <td colSpan={8 + orcamentos.length}><strong>TOTAIS</strong></td>
                                        <td colSpan={1}></td>
                                        <td><strong>{formatCurrency(totais.total_geral)}</strong></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* Legenda dos campos editáveis */}
                    {!loading && produtos.length > 0 && (
                        <div className="mt-3 alert alert-info small">
                            <i className="ti ti-info-circle me-2"></i>
                            <strong>Nota:</strong> Os campos em <span className="text-success">verde</span> podem ser editados nesta etapa do funil.
                            A cor da margem indica a lucratividade: <span className="badge bg-danger">≤ 8%</span>, <span className="badge bg-warning">8-15%</span>, <span className="badge bg-success">&gt; 15%</span>.
                            {orcamentos.length > 0 && <> As colunas de orçamentos mostram os valores unitários de cada fornecedor.</>}
                            {!configuracao.permite_editar_fornecedor &&
                                !configuracao.permite_editar_valor_proposta &&
                                " Nenhum campo pode ser editado na etapa atual."}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal para editar produto */}
            {selectedProduto && (
                <ModalEditarProduto
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    produto={selectedProduto}
                    fornecedores={fornecedores}
                    configuracao={configuracao}
                    onSave={handleProdutoSaved}
                    onValueChange={handleProdutoValueChange}
                />
            )}

            {/* Modal para editar o número do edital */}
            <Modal
                show={showEditNumeroEditalModal}
                onHide={() => setShowEditNumeroEditalModal(false)}
                centered
                size="sm"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Número no Edital</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Informe o número/identificação do item no edital:</Form.Label>
                        <Form.Control
                            type="text"
                            value={numeroEditalInput}
                            onChange={(e) => setNumeroEditalInput(e.target.value)}
                            placeholder="Ex: Item 1.2 / Lote A3"
                        />
                        <Form.Text className="text-muted">
                            Este número ajudará a identificar o produto no edital para a proposta.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowEditNumeroEditalModal(false)}
                        disabled={editingNumeroEdital}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={saveNumeroEdital}
                        disabled={editingNumeroEdital}
                    >
                        {editingNumeroEdital ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Salvando...
                            </>
                        ) : (
                            'Salvar'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirm Delete Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <div className="modal-body">
                    <div className="text-center">
                        <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                            <i className="ti ti-trash-x fs-36 text-danger" />
                        </div>
                        <h4 className="mb-2">Remover Produto?</h4>
                        <p className="mb-0">
                            Tem certeza que deseja remover o produto <br />
                            <strong>{selectedProduto?.nome_produto}</strong> desta oportunidade?
                        </p>
                        <div className="d-flex align-items-center justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmDelete}
                            >
                                Sim, Remover
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal de Novo Orçamento */}
            <ModalNovoOrcamento
                show={showModalNovoOrcamento}
                onHide={() => setShowModalNovoOrcamento(false)}
                opportunityId={opportunityId}
                orcamento={null}
                onSave={() => {
                    loadProdutos();
                    onUpdateComplete();
                }}
                formatCurrency={formatCurrency}
            />
        </>
    );
};

export default OrcamentoList;